/// <reference types="@mui/x-date-pickers/themeAugmentation" />
import { type ControlSize, adminCssVar as cssVar } from '@lions/ui-tokens';
import type { Components, CSSObject, Theme } from '@mui/material/styles';

/**
 * Control heights, enforced in the theme (DS-01): every button, input, select, autocomplete, date
 * field and toggle button of one size has the same height, so they align in a row even when a
 * screen uses raw MUI. Sizes map sm → `small`, md → `medium` (default), lg → `large`; the
 * heights, side padding and font sizes come from the `--control-*` tokens.
 *
 * Single-line inputs get a fixed height. Multiline inputs and the autocomplete (whose chips wrap)
 * get a min-height, so one row matches the control height and more rows grow.
 */

declare module '@mui/material/TextField' {
  interface TextFieldPropsSizeOverrides {
    large: true;
  }
}
declare module '@mui/material/InputBase' {
  interface InputBasePropsSizeOverrides {
    large: true;
  }
}
declare module '@mui/material/FormControl' {
  interface FormControlPropsSizeOverrides {
    large: true;
  }
}
declare module '@mui/material/InputLabel' {
  interface InputLabelPropsSizeOverrides {
    large: true;
  }
}
declare module '@mui/material/Autocomplete' {
  interface AutocompletePropsSizeOverrides {
    large: true;
  }
}

export type MuiControlSize = 'small' | 'medium' | 'large';
export const MUI_SIZE = {
  sm: 'small',
  md: 'medium',
  lg: 'large',
} as const satisfies Record<ControlSize, MuiControlSize>;

/**
 * MUI X pickers only know small and medium; the `DatePicker` wrapper marks large fields with this
 * attribute so the theme can size them.
 */
export const CONTROL_SIZE_ATTRIBUTE = 'data-control-size';

const h = (size: ControlSize) => cssVar(`control-h-${size}`);
const px = (size: ControlSize) => cssVar(`control-px-${size}`);
const font = (size: ControlSize) => cssVar(`control-font-${size}`);
/** Vertical padding that centres one line of input text (MUI line height 1.4375em) in a control. */
const centreLine = (size: ControlSize) => `calc((${h(size)} - 1.4375em) / 2)`;

const focusRing: CSSObject = {
  outline: `2px solid ${cssVar('focus-ring')}`,
  outlineOffset: 2,
};

/** Size rules for one size; `md` is applied to the root, sm and lg nest under their size class. */
function inputSize(size: ControlSize): CSSObject {
  return {
    fontSize: font(size),
    // The autocomplete root (wrapping chips) gets a min-height from MuiAutocomplete instead.
    '&:not(.MuiInputBase-multiline):not(.MuiAutocomplete-inputRoot)': { height: h(size) },
    '&.MuiInputBase-multiline': {
      height: 'auto',
      minHeight: h(size),
      paddingBlock: centreLine(size),
      paddingInline: px(size),
    },
    '& .MuiInputBase-input': { paddingBlock: 0, paddingInline: px(size) },
    '&.MuiInputBase-multiline .MuiInputBase-input': { paddingInline: 0 },
    '&.MuiInputBase-adornedStart': { paddingLeft: px(size) },
    '&.MuiInputBase-adornedEnd': { paddingRight: px(size) },
  };
}

function autocompleteSize(size: ControlSize): CSSObject {
  return {
    height: 'auto',
    minHeight: h(size),
    paddingBlock: cssVar('space-1'),
    paddingLeft: px(size),
    '& .MuiAutocomplete-input': { paddingBlock: 0, paddingInline: cssVar('space-1') },
  };
}

function pickerSize(size: ControlSize): CSSObject {
  return {
    height: h(size),
    fontSize: font(size),
    paddingInline: px(size),
    '& .MuiPickersInputBase-sectionsContainer': { paddingBlock: 0 },
  };
}

function labelAt(size: ControlSize): CSSObject {
  return { transform: `translate(${px(size)}, ${centreLine(size)}) scale(1)` };
}

function buttonSize(size: ControlSize): CSSObject {
  return { height: h(size), paddingBlock: 0, paddingInline: px(size), fontSize: font(size) };
}

function squareSize(size: ControlSize): CSSObject {
  return { width: h(size), height: h(size) };
}

export const controlComponents: Components<Theme> = {
  MuiButtonBase: {
    styleOverrides: { root: { '&.Mui-focusVisible': focusRing } },
  },
  MuiButton: {
    defaultProps: { disableElevation: true },
    styleOverrides: {
      root: {
        ...buttonSize('md'),
        minWidth: 0,
        borderRadius: cssVar('control-radius'),
        fontWeight: 700,
        lineHeight: 1.25,
        whiteSpace: 'nowrap',
        '&.MuiButton-sizeSmall': buttonSize('sm'),
        '&.MuiButton-sizeLarge': buttonSize('lg'),
      },
    },
  },
  MuiIconButton: {
    styleOverrides: {
      root: {
        ...squareSize('md'),
        padding: 0,
        borderRadius: cssVar('control-radius'),
        '&.MuiIconButton-sizeSmall': squareSize('sm'),
        '&.MuiIconButton-sizeLarge': squareSize('lg'),
        // Icon buttons inside a field (clear, show password, open calendar, autocomplete
        // indicators) keep the field's height instead of the control height.
        '.MuiInputAdornment-root &, .MuiAutocomplete-endAdornment &': {
          width: 'auto',
          height: 'auto',
          padding: cssVar('space-1'),
        },
      },
    },
  },
  MuiToggleButton: {
    styleOverrides: {
      root: {
        height: h('md'),
        minWidth: h('md'),
        paddingBlock: 0,
        paddingInline: cssVar('space-2'),
        '&.MuiToggleButton-sizeSmall': { height: h('sm'), minWidth: h('sm') },
        '&.MuiToggleButton-sizeLarge': { height: h('lg'), minWidth: h('lg') },
      },
    },
  },
  MuiOutlinedInput: {
    styleOverrides: {
      root: {
        ...inputSize('md'),
        borderRadius: cssVar('control-radius'),
        backgroundColor: cssVar('surface'),
        '&.MuiInputBase-sizeSmall': inputSize('sm'),
        '&.MuiInputBase-sizeLarge': inputSize('lg'),
        // The select's clickable area fills the control.
        '& .MuiSelect-select.MuiInputBase-input': {
          height: '100%',
          minHeight: 0,
          display: 'flex',
          alignItems: 'center',
          boxSizing: 'border-box',
        },
        '& .MuiOutlinedInput-notchedOutline': { borderColor: cssVar('border-strong') },
        '&:hover:not(.Mui-disabled, .Mui-error) .MuiOutlinedInput-notchedOutline': {
          borderColor: cssVar('text-muted'),
        },
        // Gold on white fails the 3:1 focus-indicator contrast, so focus uses the focus ring.
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
          borderColor: cssVar('focus-ring'),
          borderWidth: 2,
        },
        '&.Mui-error .MuiOutlinedInput-notchedOutline': { borderColor: cssVar('danger-text') },
      },
    },
  },
  MuiAutocomplete: {
    styleOverrides: {
      inputRoot: {
        ...autocompleteSize('md'),
        '&.MuiInputBase-sizeSmall': autocompleteSize('sm'),
      },
    },
    // Autocomplete passes only `small` on to its text field, so `large` is sized from its own prop.
    variants: [
      {
        props: { size: 'large' },
        style: {
          '& .MuiAutocomplete-inputRoot.MuiInputBase-root': autocompleteSize('lg'),
          '& .MuiInputLabel-outlined:not(.MuiInputLabel-shrink)': labelAt('lg'),
        },
      },
    ],
  },
  MuiPickersOutlinedInput: {
    styleOverrides: {
      root: {
        ...pickerSize('md'),
        borderRadius: cssVar('control-radius'),
        backgroundColor: cssVar('surface'),
        '&.MuiPickersInputBase-inputSizeSmall': pickerSize('sm'),
        [`[${CONTROL_SIZE_ATTRIBUTE}="lg"] &`]: pickerSize('lg'),
        '& .MuiPickersOutlinedInput-notchedOutline': { borderColor: cssVar('border-strong') },
        '&:hover:not(.Mui-disabled, .Mui-error) .MuiPickersOutlinedInput-notchedOutline': {
          borderColor: cssVar('text-muted'),
        },
        '&.Mui-focused .MuiPickersOutlinedInput-notchedOutline': {
          borderColor: cssVar('focus-ring'),
          borderWidth: 2,
        },
        '&.Mui-error .MuiPickersOutlinedInput-notchedOutline': {
          borderColor: cssVar('danger-text'),
        },
      },
    },
  },
  MuiInputLabel: {
    styleOverrides: {
      // Floating labels are a fallback (FormField puts the label above); centre them per size.
      root: {
        '&.MuiInputLabel-outlined:not(.MuiInputLabel-shrink)': labelAt('md'),
        '&.MuiInputLabel-outlined.MuiInputLabel-sizeSmall:not(.MuiInputLabel-shrink)':
          labelAt('sm'),
        '&.MuiInputLabel-outlined.MuiInputLabel-sizeLarge:not(.MuiInputLabel-shrink)':
          labelAt('lg'),
        [`[${CONTROL_SIZE_ATTRIBUTE}="lg"] &.MuiInputLabel-outlined:not(.MuiInputLabel-shrink)`]:
          labelAt('lg'),
      },
    },
  },
};
